import React, { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  OutlinedInput,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Button } from '@mui/material'
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'
import { LayoutMapDetailResponse, SlotDetail } from '../types'
import {
  ProductsCellRef,
  MULTI_BRAND_COLOR,
  UNKNOWN_CATEGORY_COLOR,
} from '../config/constants'
import { formatOccupancy, formatUnits, getBatchExpiryVisual } from '../config/utils'
import { productsModalStyles as styles } from '../styles/ProductsModal.styles'
import { getLayoutDetail } from '../api'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant='caption' color='text.secondary' display='block' sx={styles.statLabel}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}

function StatValue({ value, unit }: { value: string; unit?: string }) {
  return (
    <Typography fontWeight='bold' variant='body1'>
      {value}
      {unit && (
        <Typography component='span' variant='body2' sx={styles.statUnit}>
          {unit}
        </Typography>
      )}
    </Typography>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

export function ProductsModal({
  open,
  warehouseId,
  cell,
  brandColors,
  onClose,
}: {
  open: boolean
  warehouseId: number
  cell: ProductsCellRef
  brandColors: Map<string, string>
  onClose: () => void
}) {
  const { state, callApi } = useApi<LayoutMapDetailResponse>()
  const [showEan, setShowEan] = useState(false)
  const [skuFilter, setSkuFilter] = useState('')
  const [brandFilter, setBrandFilter] = useState('')
  const [viewMode, setViewMode] = useState<'visual' | 'tabla'>('visual')
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      callApi(getLayoutDetail(warehouseId, cell.aisle, cell.column, cell.level)).catch(() => {})
      setSkuFilter('')
      setBrandFilter('')
      setShowEan(false)
      setViewMode('visual')
      setSelectedSlot(null)
    }
  }, [open, warehouseId, cell.aisle, cell.column, cell.level])

  const data = state.data as LayoutMapDetailResponse | undefined
  const slots = data?.slots ?? []

  const slotGroups = useMemo(() => {
    const groups = new Map<string, { qty: number; brands: string[] }>()
    for (const s of slots) {
      const key = s.slot ?? '—'
      const existing = groups.get(key)
      if (existing) {
        existing.qty += s.qty
        if (!existing.brands.includes(s.brand)) existing.brands.push(s.brand)
      } else {
        groups.set(key, { qty: s.qty, brands: [s.brand] })
      }
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true })
    )
  }, [slots])

  const filteredSlots = useMemo((): SlotDetail[] => {
    if (!data) return []
    const lsku = skuFilter.toLowerCase()
    const lbrand = brandFilter.toLowerCase()
    return slots
      .filter((s) => {
        const slotMatch = selectedSlot === null || s.slot === selectedSlot
        const skuMatch = lsku === '' || s.sku.toLowerCase().includes(lsku) || s.ean.toLowerCase().includes(lsku)
        const brandMatch = lbrand === '' || s.brand.toLowerCase().includes(lbrand)
        return slotMatch && skuMatch && brandMatch
      })
      .slice()
      .sort(
        (a, b) =>
          a.brand.localeCompare(b.brand) ||
          a.sku.localeCompare(b.sku) ||
          (a.slot ?? '').localeCompare(b.slot ?? '', undefined, { numeric: true }) ||          
          (a.batchNumber ?? '').localeCompare(b.batchNumber ?? '', undefined, { numeric: true })
      )
  }, [data, slots, selectedSlot, skuFilter, brandFilter])

  const distinctBrands = useMemo(() => new Set(slots.map((s) => s.brand)).size, [slots])

  const titleLine = cell.level
    ? `Pasillo ${cell.aisle} · Nivel ${cell.level} · Columna ${cell.column}`
    : `Pasillo ${cell.aisle} · Columna ${cell.column}`

  const stock = data?.totalStock ?? cell.totalStock
  const capacity = data?.maxCapacity ?? cell.maxCapacity
  const pct = data?.occupancyPct ?? cell.occupancyPct
  const hasCapacity = capacity > 0

  const zones = data?.zones ?? []
  const isMultiZone = data?.isMultiZone ?? false
  const hasNamedSlots = (data?.hasSlots ?? false) && slots.some((s) => s.slot !== null)
  const showVisual = hasNamedSlots && viewMode === 'visual'
  const showTabla = !hasNamedSlots || viewMode === 'tabla'

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={styles.header}>
        <Box>
          <Typography fontWeight='bold' variant='h6' sx={styles.headerTitle}>
            {titleLine}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size='small' sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {state.status === Status.Loading && (
          <Box sx={styles.loadingWrapper}>
            <CircularProgress size={32} />
          </Box>
        )}
        {state.status === Status.Error && (
          <Typography color='error' sx={styles.errorText}>
            {state.error ?? 'Error al cargar detalle'}
          </Typography>
        )}

        {data && (
          <Fragment>
            {/* Métricas de la posición */}
            <Box sx={styles.statsBar}>
              <Stat label={isMultiZone ? 'Zonas' : 'Zona'}>
                {zones.length === 0 ? (
                  <Typography fontWeight='bold' variant='body1' color='text.disabled'>—</Typography>
                ) : zones.length <= 3 ? (
                  <Box sx={styles.zoneChipList}>
                    {zones.map((z) => (
                      <Chip key={z} label={z} size='small' sx={styles.zoneChip} />
                    ))}
                  </Box>
                ) : (
                  <Tooltip title={zones.join(', ')} arrow placement='top'>
                    <Typography fontWeight='bold' variant='body1' sx={styles.zoneCount}>
                      {zones.length} zonas
                    </Typography>
                  </Tooltip>
                )}
              </Stat>

              <Stat label='Marcas'>
                <StatValue value={String(distinctBrands)} />
              </Stat>

              <Stat label='Unidades totales'>
                <StatValue value={formatUnits(stock)} unit='uds.' />
              </Stat>

              <Stat label='Capacidad total'>
                {hasCapacity ? (
                  <StatValue value={formatUnits(capacity)} unit='uds.' />
                ) : (
                  <Typography fontWeight='bold' variant='body1' color='text.disabled'>—</Typography>
                )}
              </Stat>

              <Stat label='Ocupación'>
                {hasCapacity ? (
                  <StatValue value={formatOccupancy(pct)} />
                ) : (
                  <Typography fontWeight='bold' variant='body1' color='text.disabled'>—</Typography>
                )}
              </Stat>
            </Box>

            {/* Selector Visual / Tabla */}
            {hasNamedSlots && (
              <Box sx={styles.viewToggle}>
                <Button size='small' variant={viewMode === 'visual' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('visual')} sx={styles.viewToggleButton}>
                  Visual
                </Button>
                <Button size='small' variant={viewMode === 'tabla' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('tabla')} sx={styles.viewToggleButton}>
                  Tabla
                </Button>
              </Box>
            )}

            {/* Vista visual: un recuadro por slot, clickeable */}
            {showVisual && (
              <Box>
                <Typography sx={styles.slotCountLabel}>
                  {slotGroups.length} slot{slotGroups.length !== 1 ? 's' : ''} en esta posición
                </Typography>
                <Box sx={styles.slotGrid}>
                  {slotGroups.map(([slotName, { qty, brands }]) => {
                    const isMulti = brands.length > 1
                    const bg = isMulti
                      ? MULTI_BRAND_COLOR
                      : (brandColors.get(brands[0]) ?? UNKNOWN_CATEGORY_COLOR)
                    return (
                      <Box
                        key={slotName}
                        onClick={() => { setSelectedSlot(slotName === '—' ? null : slotName); setViewMode('tabla') }}
                        sx={styles.slotTile(bg)}
                      >
                        <Box sx={styles.slotNameBadge}>
                          <Typography sx={styles.slotName}>{slotName}</Typography>
                        </Box>
                        <Typography sx={styles.slotQty}>
                          {qty.toLocaleString('es-CL')}
                        </Typography>
                        <Typography sx={styles.slotBrand}>
                          {isMulti ? 'Multi-marca' : brands[0]}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}

            {/* Vista de tabla */}
            {showTabla && (
              <Fragment>
                <Box sx={styles.filterRow}>
                  <OutlinedInput size='small' placeholder='Buscar SKU/EAN...' value={skuFilter}
                    onChange={(e) => setSkuFilter(e.target.value)}
                    startAdornment={<SearchIcon sx={styles.filterIcon} />}
                    sx={styles.filterInput} />
                  <OutlinedInput size='small' placeholder='Buscar marca...' value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    startAdornment={<SearchIcon sx={styles.filterIcon} />}
                    sx={styles.filterInput} />
                </Box>

                {selectedSlot !== null && (
                  <Box sx={styles.slotFilterRow}>
                    <Typography variant='caption' color='text.secondary'>Slot:</Typography>
                    <Chip label={selectedSlot} size='small' onDelete={() => setSelectedSlot(null)}
                      sx={styles.slotFilterChip} />
                  </Box>
                )}

                {filteredSlots.length === 0 ? (
                  <Typography color='text.secondary' sx={styles.emptyResults}>
                    {slots.length === 0
                      ? 'No hay productos en esta posición'
                      : 'Sin resultados para los filtros aplicados'}
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant='outlined' sx={styles.tableContainer}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          {hasNamedSlots && (
                            <TableCell sx={styles.headerCellSlot}>Slot</TableCell>
                          )}
                          <TableCell sx={styles.headerCellToggle}>
                            <Box sx={styles.toggleRow}>
                              <Typography variant='caption' fontWeight='bold' color={!showEan ? 'primary.main' : 'text.disabled'} sx={styles.toggleLabel}>SKU</Typography>
                              <Switch size='small' checked={showEan} onChange={(e) => setShowEan(e.target.checked)} />
                              <Typography variant='caption' fontWeight='bold' color={showEan ? 'primary.main' : 'text.disabled'} sx={styles.toggleLabel}>EAN</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={styles.headerCell}>Marca</TableCell>
                          <TableCell align='right' sx={styles.headerCell}>Uds</TableCell>
                          <TableCell align='center' sx={styles.headerCellBatch}>Lote</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSlots.map((s) => {
                          const dotColor = brandColors.get(s.brand) ?? UNKNOWN_CATEGORY_COLOR
                          const expiry =
                            s.batchState !== null ? getBatchExpiryVisual(s.batchState) : null                          
                          const batchInfo = [
                            expiry?.label,
                            s.expirationDate ? `vence ${formatDate(s.expirationDate)}` : null,
                          ].filter(Boolean)
                          return (
                            <TableRow key={`${s.slot ?? '—'}-${s.sku}-${s.batchId ?? 'nb'}`} hover>
                              {hasNamedSlots && (
                                <TableCell sx={styles.slotCell}>
                                  {s.slot ?? '—'}
                                </TableCell>
                              )}
                              <TableCell sx={styles.skuCell}>
                                {showEan ? (s.ean || '—') : s.sku}
                              </TableCell>
                              <TableCell>
                                <Box sx={styles.brandCell}>
                                  <Box sx={styles.brandDot(dotColor)} />
                                  <Typography variant='body2'>{s.brand}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align='right'>
                                <Typography variant='body2' fontWeight={500}>{s.qty.toLocaleString('es-CL')}</Typography>
                              </TableCell>
                              <TableCell align='center'>
                                {s.batchNumber === null ? (
                                  <Typography variant='caption' color='text.disabled'>—</Typography>
                                ) : (
                                  <Tooltip
                                    title={batchInfo.length > 0 ? batchInfo.join(' · ') : 'Sin fecha de vencimiento'}
                                    arrow
                                    placement='left'
                                  >
                                    <Chip label={s.batchNumber} size='small' sx={styles.batchChip(expiry)} />
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Fragment>
            )}
          </Fragment>
        )}
      </DialogContent>
    </Dialog>
  )
}
